import { combineReducers } from 'redux';
import { SidebarApp } from '~/sidebar/sidebarApp.enum';
import { createReportReducer } from '~/store/frontendState/createReport/createReport.reducer';
import { drawingItemsContextMenuReducer } from '~/store/frontendState/drawingItemsContextMenu/drawingItemsContextMenu.reducer';
import { limitNotificationsReducer } from '~/store/frontendState/limitNotifications/limitNotifications.reducer';
import { mapListingReducer } from '~/store/frontendState/mapListing/mapListing.reducer';
import { mobileUIReducer } from '~/store/frontendState/mobileUI/mobileUI.reducer';
import { MAP_RESET } from '../map/map.actionTypes';
import { activeMapElementsReducer } from './activeMapElements/activeMapElements.reducer';
import { clientReducer } from './client/client.reducer';
import { deviceInfoReducer } from './deviceInfo/deviceInfo.reducer';
import { entityAccessInvitationsReducer } from './entityAccessInvitations/entityAccessInvitations.reducer';
import { fileUrlsReducer } from './fileUrls/fileUrls.reducer';
import { graphicSettingsReducer } from './graphicSettings/graphicSettings.reducer';
import {
  leftSidebarEmptyState, leftSidebarReducer,
} from './leftSidebar/leftSidebar.reducer';
import { mapComponentReducer } from './mapComponent/mapComponent.reducer';
import { mapInitializationReducer } from './mapInitializationFailed/mapInitializationFailed.reducer';
import { mapPresentationalReducer } from './mapPresentational/mapPresentational.reducer';
import { mapToolsReducer } from './mapTools/mapTools.reducer';
import { mapZIndexesReducer } from './mapZIndexes/mapZIndexes.reducer';
import { markersContextMenuReducer } from './markersContextMenu/markersContextMenu.reducer';
import { moveMarkerLabelsReducer } from './moveMarkerLabels/moveMarkerLabels.reducer';
import { moveMarkersReducer } from './moveMarkers/moveMarkers.reducer';
import { navigationReducer } from './navigation/navigation.reducer';
import { placesReducer } from './places/places.reducer';
import { processingReducer } from './processing/processing.reducer';
import { proximityDriveTimePathsReducer } from './proximityDriveTimePaths/proximityDriveTimePaths.reducer';
import { proximityToolReducer } from './proximityTool/proximityTool.reducer';
import { spreadsheetTableReducer } from './spreadsheetTable/spreadsheetTable.reducer';

const regularReducer = combineReducers({
  activeMapElements: activeMapElementsReducer,
  client: clientReducer,
  createReport: createReportReducer,
  deviceInfo: deviceInfoReducer,
  drawingItemsContextMenu: drawingItemsContextMenuReducer,
  entityAccessInvitations: entityAccessInvitationsReducer,
  fileUrls: fileUrlsReducer,
  graphicSettings: graphicSettingsReducer,
  leftSidebar: leftSidebarReducer,
  limitNotifications: limitNotificationsReducer,
  mapComponent: mapComponentReducer,
  mapInitializationFailed: mapInitializationReducer,
  mapListing: mapListingReducer,
  mapPresentational: mapPresentationalReducer,
  mapTools: mapToolsReducer,
  mapZIndexes: mapZIndexesReducer,
  markersContextMenu: markersContextMenuReducer,
  mobileUI: mobileUIReducer,
  moveMarkerLabels: moveMarkerLabelsReducer,
  moveMarkers: moveMarkersReducer,
  navigation: navigationReducer,
  places: placesReducer,
  processing: processingReducer,
  proximityDriveTimePaths: proximityDriveTimePathsReducer,
  proximityTool: proximityToolReducer,
  spreadsheetTable: spreadsheetTableReducer,
});

type FrontendStateState = ReturnType<typeof regularReducer>;

export const frontendStateReducer = (state: FrontendStateState, action: Action): FrontendStateState => {
  if (action.type === MAP_RESET) {
    let newState = {} as Partial<FrontendStateState>;

    if (state?.deviceInfo) {
      newState = {
        ...newState,
        deviceInfo: state.deviceInfo,
      };
    }

    if (state?.leftSidebar) {
      newState = {
        ...newState,
        leftSidebar: {
          ...leftSidebarEmptyState,
          activeMapTemplate: state.leftSidebar.activeMapTemplate,
          activeMapTemplateSelectionId: state.leftSidebar.activeMapTemplateSelectionId,
          openedApp: state.leftSidebar.openedApp === SidebarApp.MapTools ? null : state.leftSidebar.openedApp,
        },
      };
    }

    // we don't want to reset drive time paths, it's cached on BE anyway
    if (state?.proximityDriveTimePaths) {
      newState = {
        ...newState,
        proximityDriveTimePaths: state.proximityDriveTimePaths,
      };
    }

    // we don't want to reset map entityAccessInvitations
    if (state?.proximityDriveTimePaths) {
      newState = {
        ...newState,
        entityAccessInvitations: state.entityAccessInvitations,
      };
    }

    (state as Partial<FrontendStateState>) = newState;
  }

  return regularReducer(state, action);
};
